import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import notify from '../../../utils/notify';
import { Save, ArrowLeft, Plus, Printer } from 'lucide-react';

import performanceService from '../../../services/performanceService';
import MainLayout from '../../layout/MainLayout';

const criteriaList = [
    { key: 'problemIdentification', label: 'Problem Identification' },
    { key: 'potentialSolution', label: 'Potential Solution' },
    { key: 'detailing', label: 'Detailing of how to build solution' },
    { key: 'implementation', label: 'Implementation' },
    { key: 'problemSynthesizing', label: 'Problem Synthesizing' },
    { key: 'punctuality', label: 'Punctuality' },
];

const getCriteriaAverages = (evalData) => {
    if (!evalData) return criteriaList.map(c => ({ ...c, avg: 0 }));
    return criteriaList.map(c => {
        const values = evalData[c.key] || [];
        const sum = values.reduce((a, b) => a + (b || 0), 0);
        const avg = values.length > 0 ? sum / values.length : 0;
        return {
            ...c,
            avg: parseFloat(avg.toFixed(2))
        };
    });
};

const getOverallAverage = (averages) => {
    const sum = averages.reduce((acc, curr) => acc + curr.avg, 0);
    return averages.length > 0 ? (sum / averages.length).toFixed(2) : '0.00';
};

const DonutChart = ({ title, evalData }) => {
    const averages = getCriteriaAverages(evalData);
    const overallAvg = getOverallAverage(averages);
    const totalAvgSum = averages.reduce((sum, item) => sum + item.avg, 0);

    const colors = [
        '#6A4E9E', // Problem Identification
        '#00B0FF', // Potential Solution
        '#00E676', // Detailing
        '#FF8F00', // Implementation
        '#FF1744', // Problem Synthesizing
        '#E040FB', // Punctuality
    ];

    let accumulatedLength = 0;

    return (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col items-center flex-1 shadow-sm">
            <h4 className="text-lg font-semibold text-gray-700 mb-6">{title}</h4>
            
            {totalAvgSum === 0 ? (
                <div className="h-48 flex flex-col items-center justify-center text-gray-400">
                    <p className="text-sm">No evaluation data available yet.</p>
                </div>
            ) : (
                <div className="w-full flex flex-col md:flex-row items-center justify-center gap-8">
                    {/* SVG Donut */}
                    <div className="relative w-40 h-40 flex-shrink-0">
                        <svg viewBox="0 0 120 120" className="w-full h-full transform -rotate-90">
                            <circle
                                cx="60"
                                cy="60"
                                r="50"
                                fill="transparent"
                                stroke="#f3f4f6"
                                strokeWidth="12"
                            />
                            {averages.map((item, index) => {
                                if (item.avg === 0) return null;
                                const percentage = item.avg / totalAvgSum;
                                const strokeLength = percentage * 314.16;
                                const strokeOffset = -accumulatedLength;
                                accumulatedLength += strokeLength;

                                return (
                                    <circle
                                        key={item.key}
                                        cx="60"
                                        cy="60"
                                        r="50"
                                        fill="transparent"
                                        stroke={colors[index % colors.length]}
                                        strokeWidth="12"
                                        strokeDasharray={`${strokeLength} 314.16`}
                                        strokeDashoffset={strokeOffset}
                                        className="transition-all duration-500 ease-out"
                                    />
                                );
                            })}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Overall</span>
                            <span className="text-2xl font-black text-gray-800">{overallAvg}</span>
                            <span className="text-[9px] text-gray-400 font-medium">out of 5.0</span>
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="flex-grow flex flex-col gap-2 w-full">
                        {averages.map((item, index) => (
                            <div key={item.key} className="flex justify-between items-center text-xs">
                                <div className="flex items-center gap-2">
                                    <span 
                                        className="w-2.5 h-2.5 rounded-full shrink-0" 
                                        style={{ backgroundColor: colors[index % colors.length] }} 
                                    />
                                    <span className="text-gray-600 font-medium line-clamp-1">{item.label}</span>
                                </div>
                                <span className="text-gray-800 font-bold ml-2">{item.avg.toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


const PerformanceDetail = ({ mode = 'view', type = 'self' }) => {
    const { studentId, courseId } = useParams();
    const navigate = useNavigate();

    const [performance, setPerformance] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);



    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            let response;

            // IF current user is a student viewing their own stuff
            if (!studentId) {
                response = await performanceService.getMyPerformance();
                const perf = response.data.find(p => p.courseId._id === courseId);
                setPerformance(perf);
                const evalSource = type === 'self' ? perf.selfEvaluation : perf.instructorAssessment;
                setData(perf ? JSON.parse(JSON.stringify(evalSource)) : null);
            }
            // IF current user is instructor/super viewing a student
            else {
                // Use teacher-accessible endpoint (getAllPerformance for supers, or we can use getInstructorPerformance)
                // For simplicity and to avoid auth issues, let's use the one that works for the current role
                response = await performanceService.getAllPerformance({ courseId });
                const perf = response.data.find(p => p.studentId._id === studentId);
                setPerformance(perf);
                const evalSource = type === 'self' ? perf.selfEvaluation : perf.instructorAssessment;
                setData(perf ? JSON.parse(JSON.stringify(evalSource)) : null);
            }
        } catch (err) {
            notify.error(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    }, [studentId, courseId, type]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getNumRows = (evalData) => {
        if (!evalData) return 0;
        return Math.max(
            ...criteriaList.map(c => (evalData[c.key] && evalData[c.key].length) || 0),
            (evalData.topics && evalData.topics.length) || 0,
            0
        );
    };

    const handleAddRow = () => {
        const newData = { ...data };
        const numRows = getNumRows(newData);
        criteriaList.forEach(c => {
            if (!newData[c.key]) newData[c.key] = [];
            while (newData[c.key].length <= numRows) newData[c.key].push(0);
        });
        if (!newData.topics) newData.topics = [];
        while (newData.topics.length <= numRows) newData.topics.push('');
        setData(newData);
    };

    const handleCellChange = (rowIndex, criterionKey, value) => {
        let finalValue = value;
        if (criterionKey !== 'topics') {
            let numVal = parseInt(value, 10);
            if (isNaN(numVal)) numVal = 0;
            if (numVal > 5) numVal = 5;
            if (numVal < 0) numVal = 0;
            finalValue = numVal;
        }

        const newData = { ...data };
        if (!newData[criterionKey]) newData[criterionKey] = [];
        newData[criterionKey][rowIndex] = finalValue;
        setData(newData);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            if (type === 'self') {
                await performanceService.saveSelfEvaluation(courseId, data);
            } else {
                // Check if we use instructor or super save based on path or simplified service
                await performanceService.saveInstructorAssessment(studentId, courseId, data);
            }
            notify.success('Saved successfully!');
        } catch (err) {
            notify.error(err.message || 'Error saving');
        } finally {
            setSaving(false);
        }
    };

    const numRows = getNumRows(data);
    const rows = Array.from({ length: numRows }).map((_, i) => i);

    if (loading) return <MainLayout><div className="flex justify-center mt-10"><CircularProgress /></div></MainLayout>;

    return (
        <MainLayout>
            <div className="max-w-7xl mx-auto px-4 font-poppins h-full">
                <div className="mb-8 pt-6 flex justify-between items-start no-print">
                    <div>
                        <h1 className="text-3xl font-medium text-gray-800">
                            {type === 'self' ? 'Self Evaluation' : 'Instructor Assessment'}
                        </h1>
                        <p className="text-gray-500 mt-1 font-light">
                            {performance?.courseId?.title} — {performance?.studentId?.name}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => navigate(-1)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm">
                            <ArrowLeft size={18} /> Back
                        </button>
                        <button onClick={() => window.print()} className="px-6 py-2 text-sm bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all border border-gray-200 shadow-sm">
                            Print Report
                        </button>
                        {mode === 'edit' && (
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-8 py-2 text-sm bg-theme text-white rounded-xl hover:bg-theme-dark transition-all disabled:opacity-50 shadow-md shadow-theme/20"
                            >
                                {saving ? <CircularProgress size={16} color="inherit" /> : <Save size={18} />} Save Changes
                            </button>
                        )}
                    </div>
                </div>

                {/* Printable Content Start */}
                <div className="print-container">
                    {/* Re-include header for print context */}
                    <div className="hidden print:block mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">
                            {type === 'self' ? 'Self Evaluation Report' : 'Instructor Assessment Report'}
                        </h1>
                        <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-100">
                            <p className="text-gray-700"><strong>Course:</strong> {performance?.courseId?.title}</p>
                            <p className="text-gray-700"><strong>Student:</strong> {performance?.studentId?.name}</p>
                            <p className="text-gray-700"><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-12">
                        <div className="bg-gray-50/50 px-6 py-4 border-b border-gray-100 print:bg-gray-100">
                            <h3 className="font-semibold text-gray-700">Assessment Matrix</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-sm print:table-fixed">
                                <thead>
                                    <tr className="bg-gray-50/80 border-b border-gray-100 print:bg-gray-50">
                                        <th className="border-r border-gray-100 p-4 w-16 text-center text-xs font-bold text-gray-500 uppercase tracking-wider print:p-2 print:w-10">No.</th>
                                        <th className="border-r border-gray-100 p-4 w-32 text-center align-middle font-bold text-gray-500 uppercase tracking-wider text-[10px] leading-tight print:px-1 print:py-2 print:text-[8px]">Topic</th>
                                        {criteriaList.map(c => (
                                            <th key={c.key} className="border-r border-gray-100 p-4 text-center align-middle font-bold text-gray-500 uppercase tracking-wider text-[10px] leading-tight last:border-r-0 print:px-1 print:py-2 print:text-[8px]">
                                                {c.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {rows.map(idx => (
                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="border-r border-gray-100 p-3 text-center font-bold text-gray-400 bg-gray-50/30 print:p-1">{idx + 1}</td>
                                            <td className="border-r border-gray-100 p-0 text-center">
                                                {mode === 'edit' ? (
                                                    <input
                                                        type="text"
                                                        value={(data && data.topics && data.topics[idx]) || ''}
                                                        placeholder="Topic Name"
                                                        onChange={(e) => handleCellChange(idx, 'topics', e.target.value)}
                                                        className="w-full h-full p-3 text-center bg-transparent focus:bg-theme/5 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-theme transition-colors font-semibold text-gray-800 border-0 print:p-1 print:text-xs"
                                                    />
                                                ) : (
                                                    <span className="font-semibold text-gray-700 p-3 block print:p-1 print:text-xs">{(data && data.topics && data.topics[idx]) || '-'}</span>
                                                )}
                                            </td>
                                            {criteriaList.map(c => {
                                                const val = (data && data[c.key] && data[c.key][idx]) || 0;
                                                return (
                                                    <td key={c.key} className="border-r border-gray-100 p-0 text-center last:border-r-0">
                                                        {mode === 'edit' ? (
                                                            <input
                                                                type="number"
                                                                min="0" max="5"
                                                                value={val || ''}
                                                                placeholder="-"
                                                                onChange={(e) => handleCellChange(idx, c.key, e.target.value)}
                                                                className="w-full h-full p-3 text-center bg-transparent focus:bg-theme/5 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-theme transition-colors font-semibold text-gray-800 border-0 print:p-1 print:text-xs"
                                                            />
                                                        ) : (
                                                            <span className="font-semibold text-gray-700 p-3 block print:p-1 print:text-xs">{val || '-'}</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {mode === 'edit' && (
                                        <tr className="no-print">
                                            <td colSpan={criteriaList.length + 2} className="p-0">
                                                <button
                                                    onClick={handleAddRow}
                                                    className="w-full py-4 bg-gray-50/50 hover:bg-gray-100 text-theme font-semibold flex items-center justify-center gap-2 transition-colors uppercase tracking-widest text-xs"
                                                >
                                                    <Plus size={16} /> Add Assignment Row
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot className="border-t-2 border-theme/10">
                                    <tr className="bg-theme/5 font-bold print:bg-theme/5">
                                        <td className="border-r border-gray-100 p-4 text-center uppercase tracking-widest text-[10px] text-theme print:p-2">AVG</td>
                                        <td className="border-r border-gray-100 p-4 text-center print:p-2"></td>
                                        {criteriaList.map(c => {
                                            const values = (data && data[c.key]) || [];
                                            const sum = values.reduce((a, b) => a + (b || 0), 0);
                                            const avg = values.length > 0 ? (sum / values.length).toFixed(2) : '0.00';
                                            return (
                                                <td key={c.key} className="border-r border-gray-100 p-4 text-center text-theme-dark font-black last:border-r-0 print:p-2 print:text-xs">
                                                    {avg}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                        <DonutChart title="Self Evaluation Overview" evalData={performance?.selfEvaluation} />
                        <DonutChart title="Instructor Assessment Overview" evalData={performance?.instructorAssessment} />
                    </div>
                </div>
                {/* Printable Content End */}
            </div>
            {/* Legacy snackbar removed in favor of premium toasts */}
        </MainLayout>
    );
};

export default PerformanceDetail;
